import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Copy, Eye, EyeOff, ExternalLink, X, ChevronDown, ChevronUp, GripVertical, Sparkles, Upload, Image, Video, BarChart3, FlaskConical, TrendingUp, Users, Target, Home, Mountain, Leaf, Shield, Briefcase, Heart } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { SortableGallery } from './SortableGallery';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_OPTIONS = ['Star', 'Shield', 'Clock', 'Zap', 'Check', 'Heart', 'Truck', 'Package', 'Users', 'Percent', 'Gift'];
const COLOR_OPTIONS = [
  { label: 'Navy Blue', value: 'from-[#003456] to-[#001d30]' },
  { label: 'Forest Green', value: 'from-[#2d5a27] to-[#1a3517]' },
  { label: 'Royal Purple', value: 'from-[#7b2d8e] to-[#4a1a55]' },
  { label: 'Burnt Orange', value: 'from-[#8b4513] to-[#5d2e0d]' },
  { label: 'Crimson Red', value: 'from-[#8b0000] to-[#4d0000]' },
  { label: 'Ocean Teal', value: 'from-[#008080] to-[#004d4d]' }
];

const SEGMENT_ICONS = {
  families: Home,
  growers: Leaf,
  adventurers: Mountain,
  'emergency-prep': Shield,
  'pet-lovers': Heart,
  'healthy-living': Heart,
  homesteaders: Users,
  entrepreneurs: Briefcase,
  'dietary-restrictions': Leaf,
  'community-groups': Users
};

const LandingPageManager = ({ token }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [editingPermanentPage, setEditingPermanentPage] = useState(null); // For permanent page editing
  const [aiGenerating, setAiGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showABStats, setShowABStats] = useState(null);
  const [abStats, setAbStats] = useState(null);
  const [activeTab, setActiveTab] = useState('marketing'); // marketing, permanent, analytics
  const [permanentPages, setPermanentPages] = useState([]);
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [showPageAnalytics, setShowPageAnalytics] = useState(null);
  const [pageAnalytics, setPageAnalytics] = useState(null);
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [aiCustomPrompt, setAiCustomPrompt] = useState('');
  const [formData, setFormData] = useState({
    slug: '',
    name: '',
    headline: '',
    subheadline: '',
    description: '',
    cta_text: 'Shop Now',
    cta_link: '/products',
    secondary_cta_text: 'Talk to an Expert',
    secondary_cta_link: '/contact',
    badge_text: '',
    urgency_text: '',
    bg_color: 'from-[#003456] to-[#001d30]',
    benefits: [],
    offers: [],
    show_products: false,
    show_email_signup: true,
    email_headline: "Don't Miss Out",
    email_description: 'Subscribe to get exclusive offers, tips, and updates delivered to your inbox.',
    is_active: true,
    meta_title: '',
    meta_description: '',
    // New media fields
    media: [],
    hero_video_url: '',
    hero_image_url: '',
    gallery_images: [],
    // A/B Testing
    ab_testing_enabled: false,
    ab_variants: []
  });

  useEffect(() => {
    fetchPages();
    fetchPermanentPages();
    fetchAnalyticsOverview();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/landing-pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(res.data.filter(p => !p.is_permanent));
    } catch (error) {
      console.error('Failed to fetch pages');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermanentPages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/landing-pages/permanent/segments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermanentPages(res.data);
    } catch (error) {
      console.error('Failed to fetch permanent pages');
    }
  };

  const fetchAnalyticsOverview = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/landing-pages/analytics/overview?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalyticsOverview(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics');
    }
  };

  const fetchPageAnalytics = async (pageId) => {
    try {
      const res = await axios.get(`${API_URL}/api/landing-pages/${pageId}/analytics?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPageAnalytics(res.data);
      setShowPageAnalytics(pageId);
    } catch (error) {
      toast.error('Failed to fetch analytics');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPermanentPage) {
        // Update permanent page
        await axios.post(`${API_URL}/api/landing-pages/permanent/${editingPermanentPage}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Permanent page updated');
        fetchPermanentPages();
        resetForm();
      } else if (editingPage) {
        await axios.put(`${API_URL}/api/landing-pages/${editingPage.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Landing page updated');
        fetchPages();
        resetForm();
      } else {
        await axios.post(`${API_URL}/api/landing-pages`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Landing page created');
        fetchPages();
        resetForm();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save landing page');
    }
  };

  // Load permanent page for editing
  const loadPermanentPageForEdit = async (segmentKey, segment) => {
    try {
      const res = await axios.get(`${API_URL}/api/landing-pages/permanent/${segmentKey}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const pageData = res.data;
      
      setFormData({
        slug: `lifestyle-${segmentKey}`,
        name: pageData.name || segment.title,
        headline: pageData.headline || segment.headline || '',
        subheadline: pageData.subheadline || '',
        description: pageData.description || '',
        cta_text: pageData.cta_text || 'Shop Now',
        cta_link: pageData.cta_link || '/products',
        secondary_cta_text: pageData.secondary_cta_text || 'Talk to an Expert',
        secondary_cta_link: pageData.secondary_cta_link || '/contact',
        badge_text: pageData.badge_text || '',
        urgency_text: pageData.urgency_text || '',
        bg_color: pageData.bg_color || 'from-[#003456] to-[#001d30]',
        benefits: pageData.benefits || [],
        offers: pageData.offers || [],
        show_products: pageData.show_products || false,
        show_email_signup: pageData.show_email_signup !== false,
        email_headline: pageData.email_headline || "Don't Miss Out",
        email_description: pageData.email_description || 'Subscribe to get exclusive offers, tips, and updates delivered to your inbox.',
        is_active: pageData.is_active !== false,
        meta_title: pageData.meta_title || '',
        meta_description: pageData.meta_description || '',
        media: pageData.media || [],
        hero_video_url: pageData.hero_video_url || '',
        hero_image_url: pageData.hero_image_url || '',
        gallery_images: pageData.gallery_images || [],
        ab_testing_enabled: pageData.ab_testing_enabled || false,
        ab_variants: pageData.ab_variants || []
      });
      
      setEditingPermanentPage(segmentKey);
      setEditingPage(null);
      setShowForm(true);
    } catch (error) {
      toast.error('Failed to load permanent page');
    }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this landing page?')) return;
    try {
      await axios.delete(`${API_URL}/api/landing-pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Landing page deleted');
      fetchPages();
    } catch (error) {
      toast.error('Failed to delete landing page');
    }
  };

  const handleToggle = async (pageId) => {
    try {
      const res = await axios.put(`${API_URL}/api/landing-pages/${pageId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchPages();
    } catch (error) {
      toast.error('Failed to toggle status');
    }
  };

  // AI Content Generation with Custom Prompt
  const generateAIContent = async () => {
    setAiGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/landing-page`, {
        page_type: 'marketing',
        product_focus: 'freeze dryers',
        target_audience: 'families, homesteaders',
        tone: 'professional',
        include_benefits: true,
        include_offers: true,
        custom_prompt: aiCustomPrompt || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        const content = res.data.content;
        setFormData(prev => ({
          ...prev,
          headline: content.headline || prev.headline,
          subheadline: content.subheadline || prev.subheadline,
          description: content.description || prev.description,
          badge_text: content.badge_text || prev.badge_text,
          urgency_text: content.urgency_text || prev.urgency_text,
          cta_text: content.cta_text || prev.cta_text,
          secondary_cta_text: content.secondary_cta_text || prev.secondary_cta_text,
          benefits: content.benefits || prev.benefits,
          offers: content.offers || prev.offers
        }));
        toast.success('AI content generated!');
      } else {
        toast.error('AI generation returned incomplete content');
      }
    } catch (error) {
      toast.error('Failed to generate AI content');
    } finally {
      setAiGenerating(false);
    }
  };

  // Media Upload
  const handleMediaUpload = async (e, type = 'hero') => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const res = await axios.post(`${API_URL}/api/landing-pages/upload-media`, uploadFormData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const fileUrl = `${API_URL}${res.data.file_url}`;

      if (type === 'hero') {
        if (res.data.type === 'video') {
          setFormData(prev => ({ ...prev, hero_video_url: fileUrl }));
        } else {
          setFormData(prev => ({ ...prev, hero_image_url: fileUrl }));
        }
      } else if (type === 'gallery') {
        setFormData(prev => ({
          ...prev,
          gallery_images: [...prev.gallery_images, fileUrl]
        }));
      }

      toast.success('Media uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload media');
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  // A/B Testing
  const addABVariant = () => {
    const newVariant = {
      id: `var-${Date.now()}`,
      name: `Variant ${String.fromCharCode(65 + formData.ab_variants.length)}`,
      headline: '',
      subheadline: '',
      cta_text: '',
      bg_color: '',
      weight: Math.floor(100 / (formData.ab_variants.length + 2)),
      impressions: 0,
      conversions: 0
    };
    setFormData(prev => ({
      ...prev,
      ab_variants: [...prev.ab_variants, newVariant]
    }));
  };

  const updateABVariant = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.ab_variants];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, ab_variants: updated };
    });
  };

  const removeABVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      ab_variants: prev.ab_variants.filter((_, i) => i !== index)
    }));
  };

  const fetchABStats = async (pageId) => {
    try {
      const res = await axios.get(`${API_URL}/api/landing-pages/${pageId}/ab/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAbStats(res.data);
      setShowABStats(pageId);
    } catch (error) {
      toast.error('Failed to fetch A/B stats');
    }
  };

  const handleDuplicate = async (pageId) => {
    try {
      const res = await axios.post(`${API_URL}/api/landing-pages/${pageId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Landing page duplicated');
      fetchPages();
    } catch (error) {
      toast.error('Failed to duplicate page');
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      slug: page.slug || '',
      name: page.name || '',
      headline: page.headline || '',
      subheadline: page.subheadline || '',
      description: page.description || '',
      cta_text: page.cta_text || 'Shop Now',
      cta_link: page.cta_link || '/products',
      secondary_cta_text: page.secondary_cta_text || '',
      secondary_cta_link: page.secondary_cta_link || '/contact',
      badge_text: page.badge_text || '',
      urgency_text: page.urgency_text || '',
      bg_color: page.bg_color || 'from-[#003456] to-[#001d30]',
      benefits: page.benefits || [],
      offers: page.offers || [],
      show_products: page.show_products || false,
      show_email_signup: page.show_email_signup !== false,
      email_headline: page.email_headline || "Don't Miss Out",
      email_description: page.email_description || '',
      is_active: page.is_active !== false,
      meta_title: page.meta_title || '',
      meta_description: page.meta_description || '',
      // New fields
      media: page.media || [],
      hero_video_url: page.hero_video_url || '',
      hero_image_url: page.hero_image_url || '',
      gallery_images: page.gallery_images || [],
      ab_testing_enabled: page.ab_testing_enabled || false,
      ab_variants: page.ab_variants || []
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPage(null);
    setEditingPermanentPage(null);
    setFormData({
      slug: '',
      name: '',
      headline: '',
      subheadline: '',
      description: '',
      cta_text: 'Shop Now',
      cta_link: '/products',
      secondary_cta_text: 'Talk to an Expert',
      secondary_cta_link: '/contact',
      badge_text: '',
      urgency_text: '',
      bg_color: 'from-[#003456] to-[#001d30]',
      benefits: [],
      offers: [],
      show_products: false,
      show_email_signup: true,
      email_headline: "Don't Miss Out",
      email_description: 'Subscribe to get exclusive offers, tips, and updates delivered to your inbox.',
      is_active: true,
      meta_title: '',
      meta_description: '',
      // New fields
      media: [],
      hero_video_url: '',
      hero_image_url: '',
      gallery_images: [],
      ab_testing_enabled: false,
      ab_variants: []
    });
  };

  const addBenefit = () => {
    setFormData(prev => ({
      ...prev,
      benefits: [...prev.benefits, { icon: 'Star', title: '', description: '' }]
    }));
  };

  const updateBenefit = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.map((b, i) => i === index ? { ...b, [field]: value } : b)
    }));
  };

  const removeBenefit = (index) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  const addOffer = () => {
    setFormData(prev => ({
      ...prev,
      offers: [...prev.offers, { name: '', original_price: '', sale_price: '', savings: '', link: '/products' }]
    }));
  };

  const updateOffer = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      offers: prev.offers.map((o, i) => i === index ? { ...o, [field]: value } : o)
    }));
  };

  const removeOffer = (index) => {
    setFormData(prev => ({
      ...prev,
      offers: prev.offers.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return <div className="animate-pulse p-6">Loading landing pages...</div>;
  }

  return (
    <div className="space-y-6" data-testid="landing-page-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Landing Pages</h2>
          <p className="text-gray-600">Create and manage marketing landing pages</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#003456]" data-testid="create-landing-page-btn">
          <Plus className="h-4 w-4 mr-2" /> Create Landing Page
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('marketing')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'marketing' ? 'border-[#003456] text-[#003456]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Marketing Pages
        </button>
        <button
          onClick={() => setActiveTab('permanent')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'permanent' ? 'border-[#003456] text-[#003456]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Permanent Pages
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'analytics' ? 'border-[#003456] text-[#003456]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          <BarChart3 className="h-4 w-4 inline mr-1" /> Analytics
        </button>
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {analyticsOverview && (
            <>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Eye className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Views</p>
                      <p className="text-2xl font-bold">{analyticsOverview.total_views.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <Target className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Conversions</p>
                      <p className="text-2xl font-bold">{analyticsOverview.total_conversions.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-100 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Pages</p>
                      <p className="text-2xl font-bold">{analyticsOverview.total_pages}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="p-4 border-b bg-gray-50">
                  <h3 className="font-semibold">Page Performance (Last 30 Days)</h3>
                </div>
                <table className="w-full">
                  <thead className="bg-gray-50 text-left text-sm text-gray-600">
                    <tr>
                      <th className="px-4 py-3">Page</th>
                      <th className="px-4 py-3 text-center">Views</th>
                      <th className="px-4 py-3 text-center">Conversions</th>
                      <th className="px-4 py-3 text-center">CVR</th>
                      <th className="px-4 py-3 text-center">Status</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsOverview.pages.map((page) => (
                      <tr key={page.id} className="border-t hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium">{page.name}</p>
                          <p className="text-sm text-gray-500">/{page.is_permanent ? 'lifestyle' : 'promo'}/{page.slug}</p>
                        </td>
                        <td className="px-4 py-3 text-center font-medium">{page.total_views.toLocaleString()}</td>
                        <td className="px-4 py-3 text-center font-medium text-green-600">{page.total_conversions}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-bold ${page.conversion_rate > 5 ? 'text-green-600' : page.conversion_rate > 2 ? 'text-amber-600' : 'text-gray-600'}`}>
                            {page.conversion_rate}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 text-xs rounded-full ${page.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {page.is_active ? 'Active' : 'Draft'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => fetchPageAnalytics(page.id)}>
                            <BarChart3 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Permanent Pages Tab */}
      {activeTab === 'permanent' && (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-800 text-sm">
              <strong>Permanent Pages</strong> are the lifestyle segment pages linked from your homepage. 
              Customize these pages to target specific audiences with tailored messaging.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {permanentPages.map((segment) => {
              const SegmentIcon = SEGMENT_ICONS[segment.segment_key] || Users;
              return (
                <div key={segment.segment_key} className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow" data-testid={`permanent-page-${segment.segment_key}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-[#003456]/10 rounded-lg">
                        <SegmentIcon className="h-5 w-5 text-[#003456]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{segment.title}</h4>
                        <p className="text-sm text-gray-500 mt-1">/lifestyle/{segment.segment_key}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-gray-500">
                            <Eye className="h-4 w-4 inline mr-1" />{segment.total_views} views
                          </span>
                          <span className="text-green-600">
                            <Target className="h-4 w-4 inline mr-1" />{segment.total_conversions} conv.
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {segment.has_custom_page ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Customized</span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-500">Default</span>
                      )}
                      <div className="flex items-center gap-2">
                        <a 
                          href={`/lifestyle/${segment.segment_key}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#003456] bg-[#003456]/10 rounded-lg hover:bg-[#003456]/20 transition-colors"
                          data-testid={`preview-permanent-${segment.segment_key}`}
                        >
                          <Eye className="h-3 w-3" /> Preview
                        </a>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => loadPermanentPageForEdit(segment.segment_key, segment)}
                          data-testid={`edit-permanent-${segment.segment_key}`}
                        >
                          <Edit2 className="h-4 w-4 mr-1" /> Edit
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Marketing Pages Tab */}
      {activeTab === 'marketing' && (
        <>
          {pages.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <p className="text-gray-500 mb-4">No landing pages yet</p>
              <Button onClick={() => setShowForm(true)} variant="outline">
                Create your first landing page
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {pages.map((page) => (
                <div key={page.id} className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow" data-testid={`landing-page-${page.slug}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-gray-900">{page.name}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${page.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {page.is_active ? 'Active' : 'Draft'}
                        </span>
                        {page.ab_testing_enabled && (
                          <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700 flex items-center gap-1">
                            <FlaskConical className="h-3 w-3" /> A/B Test
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">/promo/{page.slug}</p>
                      <p className="text-sm text-gray-600 mt-1">{page.headline}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span><Eye className="h-4 w-4 inline mr-1" />{page.total_views || 0} views</span>
                        <span className="text-green-600"><Target className="h-4 w-4 inline mr-1" />{page.total_conversions || 0} conv.</span>
                      </div>
                    </div>
                
                    <div className="flex items-center gap-2">
                      {page.ab_testing_enabled && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => fetchABStats(page.id)}
                          title="A/B Test Stats"
                          className="text-purple-600"
                        >
                          <BarChart3 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => fetchPageAnalytics(page.id)}
                        title="View Analytics"
                        className="text-blue-600"
                      >
                        <TrendingUp className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => window.open(`/promo/${page.slug}`, '_blank')}
                        title="Preview"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleToggle(page.id)}
                        title={page.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {page.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDuplicate(page.id)}
                        title="Duplicate"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEdit(page)}
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(page.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-10 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-10 mx-4">
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10 rounded-t-xl">
              <div>
                <h3 className="text-xl font-bold">
                  {editingPermanentPage ? 'Edit Permanent Page' : editingPage ? 'Edit Landing Page' : 'Create Landing Page'}
                </h3>
                {editingPermanentPage && (
                  <p className="text-sm text-amber-600 mt-1">Editing lifestyle segment: {editingPermanentPage}</p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={resetForm}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* AI Content Generator with Prompt Modal */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-600" />
                    <span className="font-medium text-purple-800">AI Content Generator</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => setShowAIPrompt(true)}
                      variant="outline"
                      className="border-purple-300 text-purple-700"
                      size="sm"
                    >
                      Custom Prompt
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        setAiCustomPrompt('');
                        generateAIContent();
                      }}
                      disabled={aiGenerating}
                      className="bg-purple-600 hover:bg-purple-700"
                      size="sm"
                    >
                      {aiGenerating ? 'Generating...' : 'Quick Generate'}
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-purple-600 mt-2">Auto-generate headlines, descriptions, benefits, and offers using AI. Use "Custom Prompt" for specific requirements.</p>
              </div>

              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Page Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., New Year Sale Campaign"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">URL Slug {editingPermanentPage ? '(Read Only)' : '*'}</label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm">{editingPermanentPage ? '/lifestyle/' : '/promo/'}</span>
                    <Input
                      value={editingPermanentPage || formData.slug}
                      onChange={(e) => !editingPermanentPage && setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                      placeholder="new-year-sale"
                      required={!editingPermanentPage}
                      disabled={!!editingPermanentPage}
                      className={editingPermanentPage ? 'bg-gray-100 flex-1' : 'flex-1'}
                    />
                    {(editingPermanentPage || formData.slug) && (
                      <a
                        href={editingPermanentPage ? `/lifestyle/${editingPermanentPage}` : `/promo/${formData.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-white bg-[#003456] rounded-lg hover:bg-[#001d30] transition-colors whitespace-nowrap"
                        data-testid="preview-page-btn"
                      >
                        <Eye className="h-4 w-4" /> Preview
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Media Uploads */}
              <div className="border-t pt-6">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <Upload className="h-4 w-4" /> Media Content
                </h4>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Hero Image */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Hero Image</label>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center">
                      {formData.hero_image_url ? (
                        <div className="relative">
                          <img src={formData.hero_image_url} alt="Hero" className="w-full h-32 object-cover rounded" />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute top-1 right-1 bg-white/80"
                            onClick={() => setFormData(prev => ({ ...prev, hero_image_url: '' }))}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <label className="cursor-pointer block">
                          <Image className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Upload Image</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleMediaUpload(e, 'hero')}
                            disabled={uploading}
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Hero Video */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Hero Video URL</label>
                    <div className="space-y-2">
                      <Input
                        value={formData.hero_video_url}
                        onChange={(e) => setFormData(prev => ({ ...prev, hero_video_url: e.target.value }))}
                        placeholder="https://youtube.com/watch?v=... or upload"
                      />
                      <div className="border-2 border-dashed rounded-lg p-4 text-center">
                        <label className="cursor-pointer block">
                          <Video className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Or Upload Video</span>
                          <input
                            type="file"
                            accept="video/*"
                            className="hidden"
                            onChange={(e) => handleMediaUpload(e, 'hero')}
                            disabled={uploading}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gallery Images with Drag & Drop */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium">Gallery Images</label>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        multiple
                        onChange={(e) => handleMediaUpload(e, 'gallery')}
                        disabled={uploading}
                      />
                      <Button type="button" variant="outline" size="sm" disabled={uploading} asChild>
                        <span><Plus className="h-4 w-4 mr-1" /> Add Images</span>
                      </Button>
                    </label>
                  </div>
                  <SortableGallery
                    images={formData.gallery_images}
                    onReorder={(newOrder) => setFormData(prev => ({ ...prev, gallery_images: newOrder }))}
                    onRemove={(index) => removeGalleryImage(index)}
                  />
                </div>
              </div>

              {/* Hero Section */}
              <div className="border-t pt-6">
                <h4 className="font-semibold mb-4">Hero Section</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Headline *</label>
                    <Input
                      value={formData.headline}
                      onChange={(e) => setFormData(prev => ({ ...prev, headline: e.target.value }))}
                      placeholder="New Year, New Savings"
                      required
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Subheadline</label>
                      <Input
                        value={formData.subheadline}
                        onChange={(e) => setFormData(prev => ({ ...prev, subheadline: e.target.value }))}
                        placeholder="Up to $500 Off"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Badge Text</label>
                      <Input
                        value={formData.badge_text}
                        onChange={(e) => setFormData(prev => ({ ...prev, badge_text: e.target.value }))}
                        placeholder="Limited Time Offer"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg resize-none"
                      rows={3}
                      placeholder="Describe the campaign..."
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Background Color</label>
                      <select
                        value={formData.bg_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, bg_color: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        {COLOR_OPTIONS.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Urgency Text</label>
                      <Input
                        value={formData.urgency_text}
                        onChange={(e) => setFormData(prev => ({ ...prev, urgency_text: e.target.value }))}
                        placeholder="Sale ends January 31st!"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="border-t pt-6">
                <h4 className="font-semibold mb-4">Call to Action</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Primary Button Text</label>
                    <Input
                      value={formData.cta_text}
                      onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                      placeholder="Shop Now"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Primary Button Link</label>
                    <Input
                      value={formData.cta_link}
                      onChange={(e) => setFormData(prev => ({ ...prev, cta_link: e.target.value }))}
                      placeholder="/products"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Secondary Button Text</label>
                    <Input
                      value={formData.secondary_cta_text}
                      onChange={(e) => setFormData(prev => ({ ...prev, secondary_cta_text: e.target.value }))}
                      placeholder="Talk to an Expert"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Secondary Button Link</label>
                    <Input
                      value={formData.secondary_cta_link}
                      onChange={(e) => setFormData(prev => ({ ...prev, secondary_cta_link: e.target.value }))}
                      placeholder="/contact"
                    />
                  </div>
                </div>
              </div>

              {/* Benefits */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold">Benefits ({formData.benefits.length}/4)</h4>
                  <Button type="button" variant="outline" size="sm" onClick={addBenefit} disabled={formData.benefits.length >= 4}>
                    <Plus className="h-4 w-4 mr-1" /> Add Benefit
                  </Button>
                </div>
                <div className="space-y-4">
                  {formData.benefits.map((benefit, index) => (
                    <div key={index} className="flex gap-4 items-start p-3 bg-gray-50 rounded-lg">
                      <select
                        value={benefit.icon}
                        onChange={(e) => updateBenefit(index, 'icon', e.target.value)}
                        className="px-2 py-1 border rounded"
                      >
                        {ICON_OPTIONS.map(icon => (
                          <option key={icon} value={icon}>{icon}</option>
                        ))}
                      </select>
                      <Input
                        value={benefit.title}
                        onChange={(e) => updateBenefit(index, 'title', e.target.value)}
                        placeholder="Title"
                        className="flex-1"
                      />
                      <Input
                        value={benefit.description}
                        onChange={(e) => updateBenefit(index, 'description', e.target.value)}
                        placeholder="Description"
                        className="flex-1"
                      />
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeBenefit(index)}>
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Offers */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold">Special Offers ({formData.offers.length})</h4>
                  <Button type="button" variant="outline" size="sm" onClick={addOffer}>
                    <Plus className="h-4 w-4 mr-1" /> Add Offer
                  </Button>
                </div>
                <div className="space-y-4">
                  {formData.offers.map((offer, index) => (
                    <div key={index} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg flex-wrap">
                      <Input
                        value={offer.name}
                        onChange={(e) => updateOffer(index, 'name', e.target.value)}
                        placeholder="Product Name"
                        className="w-40"
                      />
                      <Input
                        type="number"
                        value={offer.original_price}
                        onChange={(e) => updateOffer(index, 'original_price', parseFloat(e.target.value) || '')}
                        placeholder="Original $"
                        className="w-28"
                      />
                      <Input
                        type="number"
                        value={offer.sale_price}
                        onChange={(e) => updateOffer(index, 'sale_price', parseFloat(e.target.value) || '')}
                        placeholder="Sale $"
                        className="w-28"
                      />
                      <Input
                        type="number"
                        value={offer.savings}
                        onChange={(e) => updateOffer(index, 'savings', parseFloat(e.target.value) || '')}
                        placeholder="Savings $"
                        className="w-28"
                      />
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeOffer(index)}>
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Settings */}
              <div className="border-t pt-6">
                <h4 className="font-semibold mb-4">Settings</h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="w-4 h-4 rounded"
                    />
                    <span>Active (visible to public)</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.show_products}
                      onChange={(e) => setFormData(prev => ({ ...prev, show_products: e.target.checked }))}
                      className="w-4 h-4 rounded"
                    />
                    <span>Show products section (if no custom offers)</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.show_email_signup}
                      onChange={(e) => setFormData(prev => ({ ...prev, show_email_signup: e.target.checked }))}
                      className="w-4 h-4 rounded"
                    />
                    <span>Show email signup section</span>
                  </label>
                </div>
              </div>

              {/* A/B Testing Section */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <FlaskConical className="h-4 w-4 text-purple-600" /> A/B Testing
                  </h4>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.ab_testing_enabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, ab_testing_enabled: e.target.checked }))}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm">Enable A/B Testing</span>
                  </label>
                </div>

                {formData.ab_testing_enabled && (
                  <div className="space-y-4 bg-purple-50 rounded-lg p-4">
                    <p className="text-sm text-purple-700">
                      Create variants to test different headlines, CTAs, or colors. Traffic will be split between variants.
                    </p>
                    
                    {formData.ab_variants.map((variant, index) => (
                      <div key={variant.id} className="bg-white rounded-lg p-4 border border-purple-200">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-medium text-purple-800">{variant.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeABVariant(index)}
                            className="text-red-500"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Alternate Headline</label>
                            <Input
                              value={variant.headline}
                              onChange={(e) => updateABVariant(index, 'headline', e.target.value)}
                              placeholder="Different headline to test"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Alternate CTA</label>
                            <Input
                              value={variant.cta_text}
                              onChange={(e) => updateABVariant(index, 'cta_text', e.target.value)}
                              placeholder="Different CTA text"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Traffic Weight (%)</label>
                            <Input
                              type="number"
                              min="1"
                              max="99"
                              value={variant.weight}
                              onChange={(e) => updateABVariant(index, 'weight', parseInt(e.target.value))}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Background Color</label>
                            <select
                              value={variant.bg_color}
                              onChange={(e) => updateABVariant(index, 'bg_color', e.target.value)}
                              className="w-full border rounded px-2 py-1 text-sm"
                            >
                              <option value="">Same as original</option>
                              {COLOR_OPTIONS.map(c => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}

                    <Button
                      type="button"
                      variant="outline"
                      onClick={addABVariant}
                      className="w-full border-purple-300 text-purple-700"
                    >
                      <Plus className="h-4 w-4 mr-2" /> Add Variant
                    </Button>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 border-t pt-6">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#003456]" data-testid="save-landing-page-btn">
                  {editingPermanentPage ? 'Save Permanent Page' : editingPage ? 'Update Page' : 'Create Page'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* A/B Stats Modal */}
      {showABStats && abStats && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-purple-600" /> A/B Test Results
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowABStats(null)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-gray-900">{abStats.total_impressions}</p>
                  <p className="text-xs text-gray-500">Total Views</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-green-600">{abStats.total_conversions}</p>
                  <p className="text-xs text-gray-500">Conversions</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-blue-600">
                    {abStats.total_impressions > 0 ? ((abStats.total_conversions / abStats.total_impressions) * 100).toFixed(1) : 0}%
                  </p>
                  <p className="text-xs text-gray-500">Overall CVR</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Variant Performance</h4>
                {abStats.variants.map((v, idx) => (
                  <div key={v.id} className={`p-3 rounded-lg mb-2 ${abStats.winner?.id === v.id ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{v.name}</span>
                      {abStats.winner?.id === v.id && (
                        <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full">Winner</span>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                      <div>
                        <span className="text-gray-500">Views:</span> {v.impressions}
                      </div>
                      <div>
                        <span className="text-gray-500">Conv:</span> {v.conversions}
                      </div>
                      <div>
                        <span className="text-gray-500">CVR:</span> {v.conversion_rate}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {!abStats.winner && abStats.variants.length > 0 && (
                <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                  Need at least 100 impressions per variant to determine a winner.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Custom Prompt Modal */}
      {showAIPrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" /> Custom AI Prompt
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowAIPrompt(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Describe what content you want to generate:
                </label>
                <textarea
                  value={aiCustomPrompt}
                  onChange={(e) => setAiCustomPrompt(e.target.value)}
                  placeholder="E.g., 'Create a landing page for our New Year sale targeting families with young children. Focus on healthy snack preservation and use an urgent, limited-time offer tone. Include 3 benefits around nutrition and convenience.'"
                  className="w-full min-h-[150px] p-3 border rounded-lg resize-y"
                />
              </div>
              
              <div className="bg-purple-50 rounded-lg p-3">
                <p className="text-sm text-purple-700">
                  <strong>Tips for better results:</strong>
                </p>
                <ul className="text-sm text-purple-600 mt-2 space-y-1">
                  <li>• Specify your target audience</li>
                  <li>• Mention the tone (urgent, friendly, professional)</li>
                  <li>• Include specific products or offers</li>
                  <li>• Describe key benefits to highlight</li>
                </ul>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowAIPrompt(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    setShowAIPrompt(false);
                    generateAIContent();
                  }}
                  disabled={aiGenerating || !aiCustomPrompt.trim()}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {aiGenerating ? 'Generating...' : 'Generate Content'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page Analytics Modal */}
      {showPageAnalytics && pageAnalytics && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-600" /> Page Analytics
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowPageAnalytics(null)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900">{pageAnalytics.page_name}</h4>
                <p className="text-sm text-gray-500">/{pageAnalytics.slug}</p>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{pageAnalytics.total_views.toLocaleString()}</p>
                  <p className="text-xs text-blue-500">Total Views</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{pageAnalytics.unique_visitors}</p>
                  <p className="text-xs text-green-500">Unique Visitors</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">{pageAnalytics.total_conversions}</p>
                  <p className="text-xs text-purple-500">Conversions</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">{pageAnalytics.conversion_rate}%</p>
                  <p className="text-xs text-amber-500">Conv. Rate</p>
                </div>
              </div>

              {pageAnalytics.conversion_breakdown && Object.keys(pageAnalytics.conversion_breakdown).length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Conversion Breakdown</h4>
                  <div className="space-y-2">
                    {Object.entries(pageAnalytics.conversion_breakdown).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
                        <span className="text-gray-700 capitalize">{type.replace('_', ' ')}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pageAnalytics.daily_stats && pageAnalytics.daily_stats.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Daily Performance (Last {pageAnalytics.days_analyzed} Days)</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left">Date</th>
                          <th className="px-4 py-2 text-center">Views</th>
                          <th className="px-4 py-2 text-center">Unique</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageAnalytics.daily_stats.slice(-7).map((day) => (
                          <tr key={day.date} className="border-t">
                            <td className="px-4 py-2">{day.date}</td>
                            <td className="px-4 py-2 text-center">{day.views}</td>
                            <td className="px-4 py-2 text-center">{day.unique_visitors}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPageManager;
